import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Filter, Loader2, RotateCcw, Heart } from 'lucide-react';
import { VERSION_LABELS, getCanonicalLaneDescriptors, getCanonicalRank, getPlacementForLane, filterAssetsForView } from '@primetime/shared';
import type { CreateUnlockRequest, CreateUnlockResponse, UserEntitlementDto } from '@primetime/shared/apiContracts';
import { api, ApiError, getAuthToken, resolveApiUrl } from '../services/apiClient';
import { PreviewPlayer } from '../components/player/PreviewPlayer';
import { PaywallNotice } from '../components/ui/PaywallNotice';
import { useAuth } from '../features/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { dispatchAccountStateSync } from '../features/account/accountStateSync';
import { useAccountCredits } from '../features/account/useAccountCredits';
import { getCreditCost } from '../features/access/catalogAccess';
import { buildCatalogDisplayRows, getCatalogVersionPreviewUrl, getPreferredVersion, hasCleanDirtyPair, type CatalogDisplayRow, type CatalogTrackRow, type CatalogTrackVersion } from '../features/catalog/displayRows';
import { canonicalAssetGroupToTrackRow, dedupeCanonicalAssetGroups, matchesCanonicalTrackQuery } from '../features/catalog/canonicalCatalog';
import { useCanonicalCatalog } from '../features/catalog/useCanonicalCatalog';

type TrackVersionRow = CatalogTrackVersion;
type TrackRow = CatalogTrackRow;
type VersionRow = CatalogDisplayRow;

type VariantSelection = { clean: boolean; dirty: boolean; djEdit: boolean };
type PendingUnlock = {
  row: VersionRow;
  version: CatalogTrackVersion;
  creditCost: number;
  idempotencyKey: string;
  isRenewal: boolean;
};
type DownloadTarget = {
  entitlementId: string;
  rowKey: string;
  version: CatalogTrackVersion;
};

const TRACK_TABLE_GRID = 'grid-cols-[64px_minmax(0,1fr)_92px] lg:grid-cols-[64px_minmax(0,1.8fr)_minmax(0,1.2fr)_120px_110px_112px_120px]';
const TRACK_TABLE_HEADER = 'border-b border-white/10 bg-white/[0.035] text-left text-[10px] font-black uppercase tracking-[0.16em] text-white/50';

function formatRank(rank?: number | null) {
  if (!rank || rank < 1) return '—';
  return String(rank).padStart(2, '0');
}

function formatDuration(seconds?: number | null) {
  if (!seconds) return '--:--';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function formatCatalogDate(value?: string | null) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function getDisplayedVersionDate(row: VersionRow) {
  return row.version.releaseDate || row.version.release_date || row.date;
}

export function TopChart100() {
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();
  const {
    creditState,
    creditStateReady,
    markInsufficientCredits,
    requiresSubscribe
  } = useAccountCredits(user?.id, authLoading);
  const inFlightActionRef = useRef<string | null>(null);
  const [selectedLaneId, setSelectedLaneId] = useState('');
  const [query, setQuery] = useState('');
  const { assetGroups: catalogAssets, status: catalogStatus } = useCanonicalCatalog();
  const canonicalAssetGroups = useMemo(() => dedupeCanonicalAssetGroups(catalogAssets), [catalogAssets]);
  const paywalled = catalogStatus === 'paywalled';
  const catalogLoading = catalogStatus === 'loading';
  const catalogEmpty = catalogStatus === 'ready' && canonicalAssetGroups.length === 0;
  const [playerTitle, setPlayerTitle] = useState('Select a track to preview');
  const [playerSubtitle, setPlayerSubtitle] = useState('Click a track name to preview');
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const [maxBpm, setMaxBpm] = useState<number>(200);
  const [variantSelections, setVariantSelections] = useState<Record<string, VariantSelection>>({});
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(() => new Set());
  const [entitlementsByVersionId, setEntitlementsByVersionId] = useState<Map<string, UserEntitlementDto>>(() => new Map());
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingUnlock, setPendingUnlock] = useState<PendingUnlock | null>(null);
  const [downloadRetry, setDownloadRetry] = useState<DownloadTarget | null>(null);

  const refreshEntitlements = useCallback(async () => {
    if (!user) {
      const empty = new Map<string, UserEntitlementDto>();
      setEntitlementsByVersionId(empty);
      return empty;
    }

    try {
      const entitlements = await api<UserEntitlementDto[]>('/api/account/entitlements');
      const indexed = new Map<string, UserEntitlementDto>();
      for (const entitlement of entitlements) {
        if (entitlement.productType !== 'TRACK_VERSION') continue;
        const trackVersionId = entitlement.trackVersionId || entitlement.productId;
        if (trackVersionId) indexed.set(trackVersionId, entitlement);
      }
      setEntitlementsByVersionId(indexed);
      return indexed;
    } catch {
      const empty = new Map<string, UserEntitlementDto>();
      setEntitlementsByVersionId(empty);
      return null;
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setWishlistIds(new Set());
      void refreshEntitlements();
      return;
    }

    api<{ trackVersionId: string }[]>('/api/account/wishlist')
      .then((wishlist) => setWishlistIds(new Set(wishlist.map((track) => track.trackVersionId))))
      .catch(() => setWishlistIds(new Set()));

    void refreshEntitlements();
  }, [user, refreshEntitlements]);

  const lanes = useMemo(() => getCanonicalLaneDescriptors(canonicalAssetGroups), [canonicalAssetGroups]);
  const activeLaneId = lanes.some((lane) => lane.laneId === selectedLaneId) ? selectedLaneId : lanes[0]?.laneId || '';
  const tracks = useMemo(() => activeLaneId
    ? filterAssetsForView(canonicalAssetGroups, 'TOP_100', activeLaneId).map(canonicalAssetGroupToTrackRow).filter((track) => track.versions.length > 0)
    : [], [activeLaneId, canonicalAssetGroups]);

  function playPreview(row: VersionRow, variant?: 'clean' | 'dirty' | 'djEdit') {
    const selectedVersion = getPreferredVersion(row, variant);
    const previewVersion = variant
      ? selectedVersion
      : getCatalogVersionPreviewUrl(selectedVersion)
        ? selectedVersion
        : row.displayVersions.find((version: CatalogTrackVersion) => getCatalogVersionPreviewUrl(version)) || selectedVersion;
    const previewUrl = getCatalogVersionPreviewUrl(previewVersion);
    setPlayerTitle(`${row.title} — ${row.artistDisplay || row.artist} (${VERSION_LABELS[previewVersion.versionType]})`);
    setPlayerSubtitle(previewUrl ? '' : 'Preview not available');
    setActiveVideoUrl(previewUrl);
  }

  function getVariantSelection(row: VersionRow): VariantSelection {
    return variantSelections[row.rowKey] || {
      clean: Boolean(row.cleanVersion) || (!row.dirtyVersion && !row.djEditVersion),
      dirty: Boolean(row.dirtyVersion) && !row.cleanVersion,
      djEdit: Boolean(row.djEditVersion) && !row.cleanVersion && !row.dirtyVersion
    };
  }

  function toggleVariant(row: VersionRow, variant: keyof VariantSelection) {
    if (variant === 'clean' && !row.cleanVersion) return;
    if (variant === 'dirty' && !row.dirtyVersion) return;
    if (variant === 'djEdit' && !row.djEditVersion) return;
    setVariantSelections(current => {
      const existing = current[row.rowKey] || getVariantSelection(row);
      const next = { ...existing, [variant]: !existing[variant] };
      if (!next.clean && !next.dirty && !next.djEdit) return current;
      return { ...current, [row.rowKey]: next };
    });
    playPreview(row, variant);
  }

  const sortedAndFiltered = useMemo(() => {
    const result = tracks.filter(t => {
      const matchesSearch = matchesCanonicalTrackQuery(t, query);
      const matchesBpm = typeof t.bpm !== 'number' || t.bpm <= maxBpm;
      return matchesSearch && matchesBpm;
    });

    result.sort((a, b) => {
      const genreOrder = (a.genre || '').localeCompare(b.genre || '');
      if (genreOrder !== 0) return genreOrder;
      const aRank = activeLaneId ? getCanonicalRank(getPlacementForLane(a, activeLaneId) || { rank: 0 }) : null;
      const bRank = activeLaneId ? getCanonicalRank(getPlacementForLane(b, activeLaneId) || { rank: 0 }) : null;
      return (aRank || Number.MAX_SAFE_INTEGER) - (bRank || Number.MAX_SAFE_INTEGER);
    });
    return result;
  }, [tracks, query, maxBpm]);

  const versionRows = useMemo<VersionRow[]>(() => buildCatalogDisplayRows(sortedAndFiltered), [sortedAndFiltered]);

  const clearFilters = () => {
    setMaxBpm(200);
    setQuery('');
  };

  function isPublishedVersion(version?: CatalogTrackVersion | null) {
    return version?.availability?.trim().toLowerCase() === 'available';
  }

  function getActionErrorMessage(error: unknown, fallback: string) {
    if (!(error instanceof ApiError)) return fallback;
    if (error.status === 401) return 'Your session has expired. Please log in again.';
    if (error.status === 402) return 'Not enough PrimeTime Credits are available for this unlock.';
    if (error.status === 403) return error.message || 'Active Member Access is required for this action.';
    if ([404, 409, 422, 503].includes(error.status)) return error.message || 'This track version is not currently available.';
    return error.message || fallback;
  }

  async function downloadAuthorizedVersion(entitlementId: string, version: CatalogTrackVersion) {
    const token = getAuthToken();
    const response = await fetch(resolveApiUrl(`/api/downloads/${encodeURIComponent(entitlementId)}`), {
      method: 'GET',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });

    if (!response.ok) {
      let message = response.statusText;
      try {
        const body = await response.json() as { error?: string };
        message = body.error || message;
      } catch {
        // Preserve the HTTP status when the secure download response is not JSON.
      }
      throw new ApiError(response.status, message);
    }

    const contentType = response.headers.get('content-type')?.toLowerCase() || '';
    if (contentType.includes('application/json')) {
      const result = await response.json() as { url?: string };
      if (!result.url) throw new ApiError(502, 'Unable to start secure download.');
      const anchor = document.createElement('a');
      anchor.href = result.url;
      anchor.download = version.downloadFilename || `${version.trackVersionId}.mp4`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return;
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = version.downloadFilename || `${version.trackVersionId}.mp4`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  function markDownloadCounted(versionId: string) {
    setEntitlementsByVersionId((current) => {
      const entitlement = current.get(versionId);
      if (!entitlement) return current;
      const next = new Map(current);
      const downloadsUsed = entitlement.downloadsUsed + 1;
      const remainingDownloads = Math.max(entitlement.remainingDownloads - 1, 0);
      next.set(versionId, {
        ...entitlement,
        downloadsUsed,
        remainingDownloads,
        status: remainingDownloads > 0 ? 'Available' : 'Locked'
      });
      return next;
    });
  }

  async function completeAuthorizedDownload(target: DownloadTarget) {
    await downloadAuthorizedVersion(target.entitlementId, target.version);
    markDownloadCounted(target.version.trackVersionId);
    dispatchAccountStateSync({ refreshLibrary: true });
  }

  async function startAuthorizedDownload(target: DownloadTarget) {
    if (inFlightActionRef.current) return;

    const actionKey = `${target.rowKey}:${target.version.trackVersionId}:download`;
    inFlightActionRef.current = actionKey;
    setActiveActionKey(actionKey);
    setActionError(null);
    try {
      await completeAuthorizedDownload(target);
      setDownloadRetry(null);
    } catch (error) {
      const isDownloadLimitError = error instanceof ApiError && error.status === 403 && error.message.toLowerCase().includes('limit');
      let retryTarget: DownloadTarget | null = isDownloadLimitError ? null : target;
      if (error instanceof ApiError && error.status === 404) {
        const refreshed = await refreshEntitlements();
        if (refreshed && !refreshed.has(target.version.trackVersionId)) retryTarget = null;
      }
      setDownloadRetry(retryTarget);
      setActionError(getActionErrorMessage(error, 'Unable to start secure download.'));
    } finally {
      if (inFlightActionRef.current === actionKey) {
        inFlightActionRef.current = null;
        setActiveActionKey(null);
      }
    }
  }

  function queueVersionUnlock(row: VersionRow, version: CatalogTrackVersion, isRenewal = false) {
    if (!isPublishedVersion(version)) return;
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    const creditCost = getCreditCost(version.versionType);
    if (!creditStateReady) return;
    if (requiresSubscribe(version.trackVersionId, creditCost)) {
      navigate('/pricing');
      return;
    }
    if (inFlightActionRef.current || pendingUnlock) return;

    setActionError(null);
    setDownloadRetry(null);
    setPendingUnlock({
      row,
      version,
      creditCost,
      idempotencyKey: `top100-${isRenewal ? 'renewal' : 'unlock'}-${row.assetGroupId}-${version.trackVersionId}-${crypto.randomUUID()}`,
      isRenewal
    });
  }

  async function confirmPendingUnlock() {
    const target = pendingUnlock;
    if (!target || authLoading || !user || !creditStateReady || inFlightActionRef.current) return;

    const unlockActionKey = `${target.row.rowKey}:${target.version.trackVersionId}:unlock`;
    inFlightActionRef.current = unlockActionKey;
    setActiveActionKey(unlockActionKey);
    setActionError(null);

    try {
      const request: CreateUnlockRequest = {
        productType: 'TRACK_VERSION',
        assetGroupId: target.row.assetGroupId,
        trackVersionId: target.version.trackVersionId,
        idempotencyKey: target.idempotencyKey
      };
      const result = await api<CreateUnlockResponse>('/api/unlocks', {
        method: 'POST',
        body: JSON.stringify(request)
      });

      dispatchAccountStateSync({ updatedCredits: result.updatedCredits, refreshLibrary: true });
      setPendingUnlock(null);
      setDownloadRetry(null);
      await refreshEntitlements();

      const downloadTarget: DownloadTarget = {
        entitlementId: result.entitlement.entitlementId,
        rowKey: target.row.rowKey,
        version: target.version
      };
      const downloadActionKey = `${downloadTarget.rowKey}:${downloadTarget.version.trackVersionId}:download`;
      inFlightActionRef.current = downloadActionKey;
      setActiveActionKey(downloadActionKey);

      try {
        await completeAuthorizedDownload(downloadTarget);
      } catch (error) {
        setDownloadRetry(downloadTarget);
        setActionError(getActionErrorMessage(error, 'Unlock succeeded, but the secure download could not start.'));
      } finally {
        if (inFlightActionRef.current === downloadActionKey) {
          inFlightActionRef.current = null;
          setActiveActionKey(null);
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 402) {
        await markInsufficientCredits(target.version.trackVersionId, target.creditCost);
        await refreshAuth();
      }
      setActionError(getActionErrorMessage(error, 'Unable to unlock this version.'));
    } finally {
      if (inFlightActionRef.current === unlockActionKey) {
        inFlightActionRef.current = null;
        setActiveActionKey(null);
      }
    }
  }

  function handleVersionDownload(row: VersionRow, version: CatalogTrackVersion) {
    if (!isPublishedVersion(version) || inFlightActionRef.current) return;

    const entitlement = entitlementsByVersionId.get(version.trackVersionId);
    if (!entitlement) {
      if (authLoading) return;
      const creditCost = getCreditCost(version.versionType);
      if (!creditStateReady) return;
      if (requiresSubscribe(version.trackVersionId, creditCost)) {
        navigate('/pricing');
        return;
      }
      queueVersionUnlock(row, version);
      return;
    }
    if (entitlement.remainingDownloads <= 0) {
      if (authLoading) return;
      const creditCost = getCreditCost(version.versionType);
      if (!creditStateReady) return;
      if (requiresSubscribe(version.trackVersionId, creditCost)) {
        navigate('/pricing');
        return;
      }
      queueVersionUnlock(row, version, true);
      return;
    }

    void startAuthorizedDownload({
      entitlementId: entitlement.entitlementId,
      rowKey: row.rowKey,
      version
    });
  }

  function renderVersionDownloadButton(row: VersionRow, version: CatalogTrackVersion) {
    const entitlement = entitlementsByVersionId.get(version.trackVersionId);
    const isUnlocked = Boolean(entitlement);
    const isBusy = activeActionKey === `${row.rowKey}:${version.trackVersionId}:download` || activeActionKey === `${row.rowKey}:${version.trackVersionId}:unlock`;
    const unavailable = !isPublishedVersion(version);
    const exhausted = Boolean(entitlement && entitlement.remainingDownloads <= 0);
    const creditCost = getCreditCost(version.versionType);
    const disabled = unavailable || Boolean(activeActionKey);
    const actionLabel = unavailable
      ? 'Unavailable'
      : exhausted
        ? `Renew ${creditCost} Credit${creditCost === 1 ? '' : 's'}`
        : isBusy
          ? 'Preparing...'
          : isUnlocked
            ? 'Download'
            : `Unlock ${creditCost} Cr`;

    return (
      <button
        key={version.trackVersionId}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleVersionDownload(row, version);
        }}
        disabled={disabled}
        aria-busy={isBusy}
        aria-label={`${actionLabel} ${VERSION_LABELS[version.versionType]} version for ${row.title}`}
        className={`focus-red inline-flex h-9 items-center justify-center gap-1 rounded-lg border px-2.5 text-[10px] font-black transition-all md:h-10 md:rounded-xl ${
          isUnlocked && !exhausted
            ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-200'
            : disabled
              ? 'cursor-not-allowed border-white/10 bg-white/5 text-white/25'
              : 'border-white/10 bg-white/5 text-white/65 hover:border-prime-red/60 hover:bg-prime-red/15 hover:text-white'
        }`}
      >
        {isBusy ? <Loader2 size={13} className="animate-spin" /> : isUnlocked && !exhausted ? <Download size={13} /> : null}
        <span>{VERSION_LABELS[version.versionType] === 'DJ Edit' ? 'DJ' : VERSION_LABELS[version.versionType].charAt(0)}</span>
      </button>
    );
  }

  function renderSubscribeButton() {
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          navigate('/pricing');
        }}
        className="btn-primary inline-flex h-9 items-center justify-center rounded-lg border-prime-red px-3 text-[10px] font-black uppercase tracking-wide md:h-10 md:rounded-xl"
        aria-label="Subscribe for PrimeTime Credits"
      >
        SUBSCRIBE
      </button>
    );
  }

  function renderVersionDownloadActions(row: VersionRow) {
    const versionStates = row.displayVersions.map((version) => {
      const entitlement = entitlementsByVersionId.get(version.trackVersionId);
      const isUnlocked = Boolean(entitlement);
      const exhausted = Boolean(entitlement && entitlement.remainingDownloads <= 0);
      const published = isPublishedVersion(version);
      return {
        version,
        isUnlocked,
        published,
        exhausted,
        needsSubscribe:
          (!isUnlocked || exhausted) &&
          published &&
          requiresSubscribe(version.trackVersionId, getCreditCost(version.versionType)),
        creditStatePending:
          (!isUnlocked || exhausted) && published && (authLoading || creditState.status === 'loading'),
        creditStateError:
          (!isUnlocked || exhausted) && published && Boolean(user) && creditState.status === 'error',
      };
    });
    const showSubscribe = versionStates.some(
      (state) => state.needsSubscribe || state.creditStateError,
    );

    return (
      <>
        {versionStates.map((state) => {
          if (
            (!state.isUnlocked || state.exhausted) &&
            state.published &&
            (state.needsSubscribe || state.creditStatePending || state.creditStateError)
          ) {
            return null;
          }
          return renderVersionDownloadButton(row, state.version);
        })}
        {showSubscribe && renderSubscribeButton()}
      </>
    );
  }

  async function toggleWishlist(row: VersionRow) {
    if (!user) return;

    const trackVersionId = row.version.trackVersionId;
    const isSaved = wishlistIds.has(trackVersionId);
    setWishlistIds(current => {
      const next = new Set(current);
      if (isSaved) {
        next.delete(trackVersionId);
      } else {
        next.add(trackVersionId);
      }
      return next;
    });

    try {
      await api(`/api/account/wishlist/${trackVersionId}`, { method: isSaved ? 'DELETE' : 'POST', ...(isSaved ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assetGroupId: row.assetGroupId }) }) });
    } catch {
      setWishlistIds(current => {
        const next = new Set(current);
        if (isSaved) {
          next.add(trackVersionId);
        } else {
          next.delete(trackVersionId);
        }
        return next;
      });
    }
  }

  if (paywalled) return <PaywallNotice message="Unable to load the Top 100 chart." />;

  return (
    <>
      <PreviewPlayer title={playerTitle} subtitle={playerSubtitle} videoUrl={activeVideoUrl} />
      <section className="container-shell pb-14 pt-6">
        {catalogLoading && (
          <div
            role="status"
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/55"
          >
            <span>Loading canonical catalog…</span>
          </div>
        )}
        {actionError && (
          <div
            role="alert"
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-prime-red/30 bg-prime-red/10 p-4 text-sm font-bold text-red-100"
          >
            <span>{actionError}</span>
            {downloadRetry && (
              <button
                type="button"
                onClick={() => void startAuthorizedDownload(downloadRetry)}
                disabled={Boolean(activeActionKey)}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-xs font-black text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RotateCcw size={13} /> Retry Download
              </button>
            )}
          </div>
        )}
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-black">Top Chart 100</h1>
          <p className="mt-3 text-white/55">Canonical Top 100 placement for the selected Genre/Subgenre lane.</p>
        </div>

        <div className="mx-auto mb-8 flex max-w-6xl flex-col gap-4 lg:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-4">
            <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm font-bold text-white/60">
              <span className="whitespace-nowrap">Canonical Top 100</span>
              <select
                className="form-input min-w-[180px] cursor-pointer bg-black/20 text-sm"
                value={activeLaneId}
                onChange={(event) => setSelectedLaneId(event.target.value)}
                aria-label="Select canonical ranking lane"
              >
                {lanes.length > 0 ? lanes.map((lane) => (
                    <option key={lane.laneId} value={lane.laneId}>
                      {lane.laneType === 'subgenre' ? `${lane.genre} / ${lane.subgenre}` : lane.genre}
                    </option>
                  )) : (
                    <option value="">No canonical lanes yet</option>
                  )}
              </select>
            </label>
            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-2">
              <label className="w-[90px] whitespace-nowrap text-xs font-bold text-white/50">Max BPM: {maxBpm}</label>
              <input type="range" min="0" max="200" value={maxBpm} onChange={(e) => setMaxBpm(parseInt(e.target.value))} className="bpm-range w-32" />
            </div>
          </div>
          <div className="relative max-w-[300px] flex-1">
            <input className="form-input w-full border-white/10 bg-black/20 text-sm" placeholder="Search catalog..." value={query} onChange={e => setQuery(e.target.value)} />
          </div>
        </div>

        <main className="mx-auto max-w-6xl min-w-0">
          <div className="glass min-h-[600px] overflow-hidden rounded-3xl">
            <div className="custom-scrollbar">
              <div className={`grid w-full ${TRACK_TABLE_GRID} ${TRACK_TABLE_HEADER} items-center gap-4 px-4 py-4 lg:px-6`}>
                <div className="col-start-1 lg:col-start-1">Genre Rank</div>
                <div className="col-start-2 lg:col-start-2">Track / Version</div>
                <div className="hidden lg:block lg:col-start-3">Artist</div>
                <div className="hidden lg:block lg:col-start-4">BPM / Key</div>
                <div className="hidden lg:block lg:col-start-5">Genre</div>
                <div className="hidden lg:block lg:col-start-6">Date</div>
                <div className="col-start-3 text-right lg:col-start-7">Unlock</div>
              </div>

              <div className="w-full divide-y divide-white/5">
                {catalogLoading ? (
                  <div role="status" aria-label="Loading top chart tracks" className="space-y-3 p-4 lg:p-6">
                    {[0, 1, 2, 3, 4].map((item) => (
                      <div key={item} className="grid grid-cols-[64px_minmax(0,1fr)_92px] items-center gap-3 rounded-lg border border-white/5 bg-white/[0.025] px-3 py-4 lg:grid-cols-[64px_minmax(0,1.8fr)_minmax(0,1.2fr)_120px]">
                        <div className="h-7 w-11 animate-pulse rounded bg-white/[0.08]" />
                        <div className="space-y-2">
                          <div className="h-4 w-2/3 animate-pulse rounded bg-white/[0.08]" />
                          <div className="h-3 w-1/3 animate-pulse rounded bg-white/[0.05]" />
                        </div>
                        <div className="hidden h-3 w-16 animate-pulse rounded bg-white/[0.05] lg:block" />
                        <div className="hidden h-3 w-12 animate-pulse rounded bg-white/[0.05] lg:block" />
                      </div>
                    ))}
                  </div>
                ) : versionRows.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-32 text-center text-white/40">
                    <Filter size={48} className="mb-4 opacity-10" />
                    <p className="text-lg font-bold">{catalogEmpty ? 'No tracks are available in the catalog yet.' : 'No tracks match these filters.'}</p>
                    <p className="mt-2 max-w-md text-sm text-white/45">
                      {catalogEmpty ? 'Canonical ranking lanes and tracks will appear here when they are published.' : ''}
                    </p>
                    {!catalogEmpty && (
                      <button onClick={clearFilters} className="focus-red mt-4 rounded-lg px-3 py-2 text-sm font-black text-prime-red transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-prime-red/10 hover:shadow-glow active:translate-y-0 active:scale-[0.97]">
                        <RotateCcw size={14} className="mr-1 inline-block align-text-bottom" /> Reset filters
                      </button>
                    )}
                  </div>
                ) : (
                  versionRows.map(row => {
                    const variantSelection = getVariantSelection(row);
                    const isSaved = wishlistIds.has(row.version.trackVersionId);

                    return (
                      <div key={row.rowKey} className={`grid w-full ${TRACK_TABLE_GRID} items-center gap-3 px-4 py-3 transition-colors group hover:bg-white/[0.03] lg:gap-4 lg:px-6 lg:py-5`}>
                        <div className="col-start-1 flex items-center text-left lg:col-start-1">
                          <span className="inline-flex min-w-[2.75rem] justify-center rounded-lg border border-white/10 bg-white/[0.06] px-2 py-1 text-[12px] font-black tabular-nums text-white/80">
                            {formatRank(activeLaneId ? getCanonicalRank(getPlacementForLane(row, activeLaneId) || { rank: 0 }) : null)}
                          </span>
                        </div>
                        <div className="col-start-2 min-w-0 text-left outline-none lg:col-start-2">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              playPreview(row);
                            }}
                            aria-label={`Preview ${row.title} by ${row.artistDisplay || row.artist}`}
                            className="focus-red block min-w-0 rounded-md text-left"
                          >
                            <div className="truncate font-bold text-white transition-colors group-hover:text-prime-red group-focus-within:text-prime-red">
                              {row.title} <span className="font-medium text-white/55">{row.displayVersionLabel}</span>
                            </div>
                          </button>
                          {hasCleanDirtyPair(row) && (
                          <div className="mt-2 flex items-center gap-1.5 lg:hidden">
                            {row.cleanVersion && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleVariant(row, 'clean');
                              }}
                              aria-label="Toggle clean version"
                              className={`min-w-[58px] rounded-md border px-3 py-1 text-[10px] font-black transition-colors ${variantSelection.clean ? 'border-prime-red bg-prime-red text-white shadow-glow' : 'border-white/10 bg-white/5 text-white/45'}`}
                            >
                              C
                            </button>
                            )}
                            {row.dirtyVersion && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleVariant(row, 'dirty');
                              }}
                              aria-label="Toggle dirty version"
                              className={`min-w-[58px] rounded-md border px-3 py-1 text-[10px] font-black transition-colors ${variantSelection.dirty ? 'border-prime-red bg-prime-red text-white shadow-glow' : 'border-white/10 bg-white/5 text-white/45'}`}
                            >
                              D
                            </button>
                            )}
                            {row.djEditVersion && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleVariant(row, 'djEdit');
                              }}
                              aria-label="Toggle DJ Edit version"
                              className={`min-w-[58px] rounded-md border px-3 py-1 text-[10px] font-black transition-colors ${variantSelection.djEdit ? 'border-prime-red bg-prime-red text-white shadow-glow' : 'border-white/10 bg-white/5 text-white/45'}`}
                            >
                              DJ
                            </button>
                            )}
                          </div>
                          )}
                        </div>

                        <div className="hidden min-w-0 text-xs font-bold uppercase tracking-wider text-white/40 lg:col-start-3 lg:block">
                          <div className="truncate">{row.artistDisplay || row.artist}</div>
                        </div>

                        <div className="hidden text-sm font-black text-white/80 lg:col-start-4 lg:block">
                          <div>{row.bpm ?? '--'} / {row.key || '--'}</div>
                        </div>

                        <div className="hidden min-w-0 text-xs font-medium text-white/60 lg:col-start-5 lg:block">
                          <div className="truncate">{row.genre === 'Open Format' || row.genre === 'Lounge' ? '' : [row.genre, row.subgenre].filter(Boolean).join(' / ')}</div>
                        </div>

                        <div className="hidden min-w-0 text-xs font-medium text-white/60 lg:col-start-6 lg:block">
                          <div className="truncate">{formatCatalogDate(getDisplayedVersionDate(row))}</div>
                          {hasCleanDirtyPair(row) && (
                          <div className="mt-1 flex items-center gap-1.5">
                            {row.cleanVersion && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleVariant(row, 'clean');
                              }}
                              className={`rounded-md border px-2 py-1 text-[10px] font-black transition-colors ${variantSelection.clean ? 'border-prime-red bg-prime-red text-white shadow-glow' : 'border-white/10 bg-white/5 text-white/45 hover:text-white'}`}
                            >
                              C
                            </button>
                            )}
                            {row.dirtyVersion && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleVariant(row, 'dirty');
                              }}
                              className={`rounded-md border px-2 py-1 text-[10px] font-black transition-colors ${variantSelection.dirty ? 'border-prime-red bg-prime-red text-white shadow-glow' : 'border-white/10 bg-white/5 text-white/45 hover:text-white'}`}
                            >
                              D
                            </button>
                            )}
                            {row.djEditVersion && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleVariant(row, 'djEdit');
                              }}
                              className={`rounded-md border px-2 py-1 text-[10px] font-black transition-colors ${variantSelection.djEdit ? 'border-prime-red bg-prime-red text-white shadow-glow' : 'border-white/10 bg-white/5 text-white/45 hover:text-white'}`}
                            >
                              DJ
                            </button>
                            )}
                          </div>
                          )}
                        </div>

                        <div className="col-start-3 flex flex-wrap items-center justify-end gap-2 lg:col-start-7">
                          {user && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleWishlist(row);
                              }}
                              className={`focus-red flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-all md:h-10 md:w-10 md:rounded-xl ${isSaved ? 'border-prime-red bg-prime-red/15 text-prime-red shadow-glow' : 'border-white/10 bg-white/5 text-white/45 hover:border-prime-red/50 hover:text-prime-red'}`}
                              aria-label={isSaved ? 'Remove from wishlist' : 'Add to wishlist'}
                            >
                              <Heart size={15} fill={isSaved ? 'currentColor' : 'none'} />
                            </button>
                          )}
                          {renderVersionDownloadActions(row)}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </main>
      </section>
      {pendingUnlock && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="top100-unlock-title"
          aria-describedby="top100-unlock-description"
        >
          <div className="glass w-full max-w-[460px] rounded-2xl border border-white/10 p-5 shadow-2xl">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-prime-red">PRIMETIME CREDITS</p>
            <h2 id="top100-unlock-title" className="mt-2 text-xl font-black text-white">GET 5 DOWNLOADS</h2>
            <div className="mt-3 min-w-0">
              <p className="break-words text-[15px] font-black leading-5 text-white">{pendingUnlock.row.title}</p>
              <p className="mt-1 break-words text-[12px] leading-5 text-white/60">
                {pendingUnlock.row.artistDisplay || pendingUnlock.row.artist}
                <span className="mx-2 text-white/30">•</span>
                <span className="font-black uppercase text-prime-red">
                  {pendingUnlock.row.editLabel?.trim() || pendingUnlock.row.editorName?.trim() || VERSION_LABELS[pendingUnlock.version.versionType]}
                </span>
              </p>
            </div>
            <div className="mt-3 rounded-xl border border-white/15 bg-white/[0.04] p-3 text-xs">
              <div className="flex items-center justify-between gap-4">
                <span className="text-white/55">Version</span>
                <span className="font-black text-white">{VERSION_LABELS[pendingUnlock.version.versionType]}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2">
                <span className="text-white/55">Cost</span>
                <span className="font-black text-white">{pendingUnlock.creditCost} PrimeTime {pendingUnlock.creditCost === 1 ? 'Credit' : 'Credits'}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2">
                <span className="text-white/55">Downloads included</span>
                <span className="font-black text-white">5</span>
              </div>
            </div>
            <p id="top100-unlock-description" className="mt-3 text-[12px] leading-5 text-white/60">
              Your MP4 will download automatically after confirmation. Your remaining 4 downloads will be available in My Library.
            </p>

            {actionError && <p role="alert" className="mt-4 text-sm font-bold text-red-200">{actionError}</p>}

            <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (activeActionKey) return;
                  setPendingUnlock(null);
                  setActionError(null);
                }}
                disabled={Boolean(activeActionKey)}
                className="w-full rounded-lg border border-white/15 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-white/70 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmPendingUnlock()}
                disabled={Boolean(activeActionKey)}
                className="btn-primary inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-xs font-black uppercase tracking-wider disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {activeActionKey ? <Loader2 size={16} className="animate-spin" /> : null}
                {activeActionKey
                  ? pendingUnlock.isRenewal ? 'Renewing...' : 'Unlocking...'
                  : 'Confirm & Download'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
